import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CreditCard, DollarSign, Loader2, Info } from "lucide-react";
import SquareSetupGuide from "@/components/shared/SquareSetupGuide";

export const ParentPaymentSetupCard = ({
  settings,
  onFieldChange,
  onSave,
  saving,
  squareConnected,
  onConnectSquare,
  connectingSquare,
  disabled,
}) => {
  const opts = settings.paymentOptions;

  return (
    <Card className="overflow-hidden border-2 border-blue-100 bg-white shadow-xl" data-testid="parent-payment-setup-card">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
        <CardTitle className="flex items-center gap-2 text-blue-800">
          <DollarSign className="h-5 w-5" />
          Parent Payment Options
        </CardTitle>
        <CardDescription className="text-blue-700/80">
          Set up how activated parents will pay you after their trial ends
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5 p-6">

        {/* Owner guidance panel */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2" data-testid="parent-payment-owner-guide">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Info className="h-4 w-4 text-blue-500 flex-shrink-0" /> How parent payments work</p>
          <ul className="space-y-2 text-xs text-slate-600 list-none">
            <li><span className="font-semibold text-blue-600">Zelle:</span> Parent sees your Zelle info on the upgrade screen and sends payment manually. You verify in your bank app, then click <span className="font-semibold">Activate</span> next to the child's name in the list above to grant access.</li>
            <li><span className="font-semibold text-amber-600">Square / Card:</span> Parent clicks "Pay with Card" and pays online. Money deposits directly into your Square bank account — no catalog item needed. Activates automatically via webhook.</li>
            <li><span className="font-semibold text-emerald-600">Connecting Square:</span> Click "Connect Square Account" → Square login page appears → enter your email &amp; password → click <span className="font-semibold">Allow</span> on the authorization screen → you're redirected back here automatically with your account linked.</li>
          </ul>
        </div>

        {/* Zelle */}
        <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 space-y-3">
          <p className="font-semibold text-blue-700 flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Zelle Payment Option
          </p>
          <p className="text-sm text-slate-500">Enter the email OR phone number where parents should send Zelle payment.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-blue-700 text-xs font-semibold">Zelle Email</Label>
              <Input
                type="email"
                value={opts.parentZelleEmail || ""}
                onChange={(e) => onFieldChange("parentZelleEmail", e.target.value)}
                placeholder="your-email@example.com"
                disabled={disabled}
                className="mt-1 border-blue-200 bg-white"
                data-testid="parent-payment-zelle-email"
              />
            </div>
            <div className="relative">
              <div className="absolute -left-3 top-7 bg-blue-100 text-blue-700 text-xs font-bold px-1 rounded">OR</div>
              <Label className="text-blue-700 text-xs font-semibold">Zelle Phone</Label>
              <Input
                type="tel"
                value={opts.parentZellePhone || ""}
                onChange={(e) => onFieldChange("parentZellePhone", e.target.value)}
                placeholder="412-555-1234"
                disabled={disabled}
                className="mt-1 border-blue-200 bg-white"
                data-testid="parent-payment-zelle-phone"
              />
            </div>
          </div>
        </div>

        {/* Square / Credit Card */}
        <div className={`rounded-xl border p-4 space-y-3 ${opts.parentSquareEnabled ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-slate-50/40"}`}>
          <div className="flex items-center justify-between">
            <p className={`font-semibold flex items-center gap-2 ${opts.parentSquareEnabled ? "text-amber-700" : "text-slate-500"}`}>
              <CreditCard className="h-4 w-4" />
              Credit Card Payment Option
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{opts.parentSquareEnabled ? "Enabled" : "Disabled"}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onFieldChange("parentSquareEnabled", !opts.parentSquareEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${opts.parentSquareEnabled ? "bg-amber-500" : "bg-slate-300"} disabled:opacity-50`}
                data-testid="parent-payment-square-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${opts.parentSquareEnabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
          </div>

          <SquareSetupGuide
            squareConnected={squareConnected}
            onConnect={onConnectSquare}
          />
        </div>

        <Button
          type="button"
          onClick={onSave}
          disabled={saving || disabled}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="parent-payment-save-btn"
        >
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : "Save Payment Settings"}
        </Button>
      </CardContent>
    </Card>
  );
};
